import { ConfigReader } from '@mainstage/core-app-api';
import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import { ScmIntegrationsApi } from '@mainstage/integration-react';
import { gitIntegrationsChangeNotifier } from '@agilelab/plugin-wb-platform-common';
import { JsonValue } from '@mainstage/types';

type ScmIntegrations = ReturnType<typeof ScmIntegrationsApi.fromConfig>;

const REFRESH_INTERVAL_MS = 60_000;

/**
 * Builds a `ScmIntegrationRegistry` that is populated from the
 * `wb-configuration` backend's `/git-integrations` endpoint (the database)
 * instead of Backstage's usual static read of `integrations` from
 * `app-config.yaml`.
 *
 * This mirrors the backend's `coreServices.scmIntegrations` override (see
 * specs/991-dynamic-git-integrations) on the frontend, so that consumers such
 * as the RepoUrlPicker scaffolder field and the Catalog Import page see the
 * same dynamically-configured git providers, instead of only the ones
 * hardcoded in `app-config.yaml`.
 *
 * Like its backend counterpart, this is push-based: the Platform Settings
 * "Git Providers" page calls `gitIntegrationsChangeNotifier.notifyChange()`
 * on save/delete, which triggers an immediate rebuild here — no waiting on a
 * timer. The periodic refresh below only exists as a fallback for changes
 * made from a different browser tab/session, which can't reach this
 * in-memory listener.
 *
 * The returned object is a Proxy so that callers holding a stable reference
 * (e.g. `const integrations = useApi(scmIntegrationsApiRef)`) always read
 * through to the latest rebuilt registry.
 */
export function createDynamicScmIntegrationsApi(options: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}): ScmIntegrations {
  const { discoveryApi, fetchApi } = options;

  let current: ScmIntegrations = ScmIntegrationsApi.fromConfig(
    new ConfigReader({}),
  );
  let lastFetchedAt = 0;
  let inFlight: Promise<void> | null = null;

  const refresh = async (): Promise<void> => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('wb-configuration');
      const response = await fetchApi.fetch(`${baseUrl}/git-integrations`);
      if (!response.ok) return;
      const data: Record<string, { host?: string } | undefined> =
        await response.json();

      // The API returns one config object per provider (e.g. `{ github: {...} }`);
      // `ScmIntegrationsApi.fromConfig` expects an array per provider type.
      const integrations: Record<string, JsonValue[]> = {};
      for (const [providerKey, config] of Object.entries(data)) {
        if (config?.host) {
          integrations[providerKey] = [config as JsonValue];
        }
      }

      current = ScmIntegrationsApi.fromConfig(
        new ConfigReader({ integrations }),
      );
      lastFetchedAt = Date.now();
    } catch {
      // Keep serving the last known-good integrations on failure.
    }
  };

  const forceRefresh = (): void => {
    inFlight = refresh().finally(() => {
      inFlight = null;
    });
  };

  const ensureFresh = (): void => {
    if (inFlight || Date.now() - lastFetchedAt < REFRESH_INTERVAL_MS) return;
    forceRefresh();
  };

  // Kick off the first fetch immediately so data is available as soon as
  // possible.
  ensureFresh();

  // Rebuild immediately when this page/tab changes a git integration,
  // instead of waiting up to REFRESH_INTERVAL_MS for the next poll.
  gitIntegrationsChangeNotifier.onChange(forceRefresh);

  return new Proxy({} as ScmIntegrations, {
    get(_target, prop, receiver) {
      ensureFresh();
      const value = Reflect.get(current as object, prop, receiver);
      // Bind functions to the real underlying instance: since they're
      // invoked as `proxy.method()`, `this` inside them would otherwise be
      // the (empty) proxy target rather than the real registry instance.
      return typeof value === 'function' ? value.bind(current) : value;
    },
  });
}
